/**
 * **실시간 커튼** (2026-09-15, Chris: 「지나가면 찰랑거리거나 사물이 커튼 밑에 바로 있어도 자연스럽게」).
 *
 * 구운 메시(블렌더 천 시뮬)는 예뻐도 죽어 있다 — 책상 위로 늘어뜨리면 책상을 뚫고, 사람이 지나가도
 * 그대로다. 그래서 폭마다 작은 격자(10×16 입자)를 두고 위치기반(Verlet + 거리 제약) 천을 돌린다.
 *
 * 똥컴 규칙: **보는 사람에게서 9m 안의 폭만 계산**한다. 나머지는 처음 3초 동안 자리를 잡은 뒤 멈춘다.
 * 폭 하나가 입자 160 · 제약 ~830 이라 활성 6폭이면 프레임당 몇만 번 연산 — JS 로도 1ms 안팎이다.
 *
 * 충돌: 바닥 · 벽면 · 창턱 · 근처 가구(조각 그룹의 AABB, 0.5초마다 갱신) · 사람(캡슐 두 구).
 * 주름: 윗단을 원단 폭보다 좁게 모아 고정하고(가로 제약의 쉼 길이가 실제 간격보다 길다) 아래는 자유 —
 * 천이 스스로 접힌다. 굽힘 제약이 있어서 구겨지지 않고 부드럽게 접힌다.
 */
import * as THREE from 'three';

export interface CurtainSpec {
  /** 봉 아래 윗단 가운데(월드) */
  top: THREE.Vector3;
  /** 벽을 따라가는 단위벡터(폭 방향) */
  lateral: THREE.Vector3;
  /** 방 안쪽 단위벡터 */
  inward: THREE.Vector3;
  /** 걸린 폭(모은 뒤) */
  width: number;
  /** 원단 폭 — 이보다 좁게 모으면 그 차이가 주름이 된다 */
  fabricWidth: number;
  /** 길이 */
  length: number;
  /** 바닥 높이(월드 y) */
  floorY: number;
  /** 벽면의 inward 방향 거리(폭이 벽 안으로 못 들어가게). top 기준 */
  wallBack: number;
  /** 천이 벽면에서 떨어진 거리. **창턱(16cm)보다 커야** 턱이 천을 뚫고 나오지 않는다(Chris 2026-09-15 컷). 봉과 같은 값 */
  hang: number;
  color: THREE.ColorRepresentation;
  /** 홀수 폭은 주름 방향을 뒤집어 둘이 같아 보이지 않게 */
  mirror: boolean;
}

const COLS = 16, ROWS = 12;   // 주름 5개를 사인으로 그리려면 가로가 촘촘해야 한다. 창 길이라 세로는 12
const GRAV = -9.8;
const SUBSTEPS = 2, ITERS = 3;

interface Obstacle { min: THREE.Vector3; max: THREE.Vector3 }

const matCache = new Map<string, THREE.MeshPhysicalMaterial>();
function fabricMaterial(color: THREE.ColorRepresentation) {
  const key = new THREE.Color(color).getHexString();
  let m = matCache.get(key);
  if (!m) {
    m = new THREE.MeshPhysicalMaterial({ color, roughness: 0.9, metalness: 0, sheen: 0.55, sheenRoughness: 0.8, sheenColor: new THREE.Color('#ffffff'), side: THREE.DoubleSide });
    matCache.set(key, m);
  }
  return m;
}

export class CurtainPanel {
  readonly mesh: THREE.Mesh;
  readonly spec: CurtainSpec;
  private readonly n = COLS * ROWS;
  private pos: Float32Array;
  private prev: Float32Array;
  private rest!: Float32Array;
  private cons: { a: number; b: number; rest: number; k: number }[] = [];
  private obstacles: Obstacle[] = [];
  private obstacleAge = 9;
  private geo: THREE.BufferGeometry;
  private center = new THREE.Vector3();
  private phase = Math.random() * 100;
  /**
   * **E 상호작용이 미는 슬라이드 추적** (2026-09-17, Chris: 「단서가 있든 없든 싹다 상호작용」).
   * world.ts emitCurtains 가 폭마다 `open:왼폭/오른폭` 그룹을 달아 두고, props.attachToPart 가
   * 그 그룹에 경첩을 끼운다. 경첩이 미는 거리(hinge.position.x, 호스트 로컬 = 봉 방향)만큼
   * 천 상태 전체를 lateral 으로 평행이동한다 — 시뮬은 월드 공간이라 그룹 이동이 아니라
   * 좌표를 통째로 민다. */
  slideGroup: THREE.Object3D | null = null;
  /** 지금까지 적용한 누적 슬라이드 — 경첩 로컬 x 와의 차분만큼만 translate 한다 */
  slideApplied = 0;

  constructor(spec: CurtainSpec) {
    this.spec = spec;
    this.pos = new Float32Array(this.n * 3);
    this.prev = new Float32Array(this.n * 3);
    const { top, lateral, inward, width, fabricWidth, length } = spec;
    const dx = width / (COLS - 1), dy = length / (ROWS - 1);
    /* **쉼 자세 = 첫 디자인**(Chris 2026-09-15: 「맨 처음 디자인이 제일 괜찮다」). 폭 방향 사인 5주름,
     * 아래로 갈수록 주름이 조금 펴진다. 제약의 쉼 길이를 이 자세에서 재므로 이 모양이 평형이고,
     * 밀리거나 바람에 흔들려도 여기로 돌아온다(shape spring). 주름이 스스로 접히길 기다리지 않는다 —
     * 그 방식은 텐트나 자루가 됐다. */
    this.rest = new Float32Array(this.n * 3);
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      const i = (r * COLS + c) * 3;
      const u = c / (COLS - 1), t = r / (ROWS - 1);
      const pleat = Math.sin(u * Math.PI * 2 * 5 + (spec.mirror ? Math.PI : 0)) * 0.035 * (0.55 + 0.45 * (1 - t));
      const p = top.clone().addScaledVector(lateral, -width / 2 + c * dx).addScaledVector(inward, spec.hang + pleat).addScaledVector(new THREE.Vector3(0, -1, 0), r * dy);
      this.rest[i] = p.x; this.rest[i + 1] = p.y; this.rest[i + 2] = p.z;
    }
    this.pos.set(this.rest); this.prev.set(this.rest);
    const R = this.rest;
    const dist = (a: number, b: number) => Math.hypot(R[a * 3] - R[b * 3], R[a * 3 + 1] - R[b * 3 + 1], R[a * 3 + 2] - R[b * 3 + 2]);
    const add = (a: number, b: number, k: number) => this.cons.push({ a, b, rest: dist(a, b), k });
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
      const i = r * COLS + c;
      if (c + 1 < COLS) add(i, i + 1, 1);                                          // 가로
      if (r + 1 < ROWS) add(i, i + COLS, 1);                                       // 세로
      if (c + 1 < COLS && r + 1 < ROWS) { add(i, i + COLS + 1, 0.35); add(i + 1, i + COLS, 0.35); }   // 전단
      if (c + 2 < COLS) add(i, i + 2, 0.3);                                        // 굽힘(가로) — 주름 곡률 유지
      if (r + 2 < ROWS) add(i, i + 2 * COLS, 0.5);                                 // 굽힘(세로)
    }
    void fabricWidth;
    // 지오메트리
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    const uv = new Float32Array(this.n * 2);
    for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) { uv[(r * COLS + c) * 2] = c / (COLS - 1); uv[(r * COLS + c) * 2 + 1] = 1 - r / (ROWS - 1); }
    this.geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
    const idx: number[] = [];
    for (let r = 0; r + 1 < ROWS; r++) for (let c = 0; c + 1 < COLS; c++) { const i = r * COLS + c; idx.push(i, i + COLS, i + 1, i + 1, i + COLS, i + COLS + 1); }
    this.geo.setIndex(idx);
    this.geo.computeVertexNormals();
    this.mesh = new THREE.Mesh(this.geo, fabricMaterial(spec.color));
    this.mesh.name = 'curtain'; this.mesh.castShadow = true; this.mesh.receiveShadow = true;
    // 시야 밖 폭은 그리지 않는다 — 100폭이 전부 draw call 로 나가면 그것도 렉이다. 경계구는 쉼 자세 + 여유 0.6m
    this.geo.computeBoundingSphere();
    if (this.geo.boundingSphere) this.geo.boundingSphere.radius += 0.6;
    this.mesh.frustumCulled = true;
    this.center.copy(top).addScaledVector(new THREE.Vector3(0, -1, 0), length / 2);
  }

  distanceTo(p: THREE.Vector3) { return this.center.distanceTo(p); }

  /** 폭이 봉을 따라 d m 옮겼다 — 쉼 자세·입자·기준점·충돌 기준면이 전부 같이 간다 */
  translate(d: number) {
    const v = this.spec.lateral;
    for (let i = 0; i < this.n * 3; i += 3) {
      this.pos[i] += v.x * d; this.pos[i + 2] += v.z * d;
      this.prev[i] += v.x * d; this.prev[i + 2] += v.z * d;
      this.rest[i] += v.x * d; this.rest[i + 2] += v.z * d;
    }
    this.spec.top.addScaledVector(v, d);
    this.center.addScaledVector(v, d);
    this.geo.computeBoundingSphere();
    if (this.geo.boundingSphere) this.geo.boundingSphere.radius += 0.6;
  }

  /** 근처 가구의 AABB. `pieces` 는 시스템이 만든 조각 그룹 목록 — **폭마다 씬을 훑으면 접속 직후 렉이 난다**(2026-09-15 Chris) */
  refreshObstacles(pieces: THREE.Object3D[]) {
    const box = new THREE.Box3();
    const near = new THREE.Box3().setFromCenterAndSize(this.center, new THREE.Vector3(this.spec.width + 1.2, this.spec.length + 0.6, 1.4));
    const out: Obstacle[] = [];
    const s = this.spec;
    // 창턱 — 벽면에서 16cm 내민 판
    const sillMin = s.top.clone().addScaledVector(new THREE.Vector3(0, -1, 0), 0.14 + 1.2 + 0.02).addScaledVector(s.lateral, -s.width - 0.2);
    const sillMax = s.top.clone().addScaledVector(new THREE.Vector3(0, -1, 0), 0.14 + 1.2 - 0.03).addScaledVector(s.lateral, s.width + 0.2).addScaledVector(s.inward, 0.16 + s.wallBack);
    out.push({ min: new THREE.Vector3(Math.min(sillMin.x, sillMax.x), Math.min(sillMin.y, sillMax.y), Math.min(sillMin.z, sillMax.z)), max: new THREE.Vector3(Math.max(sillMin.x, sillMax.x), Math.max(sillMin.y, sillMax.y), Math.max(sillMin.z, sillMax.z)) });
    const wp = new THREE.Vector3();
    for (const o of pieces) {
      if (out.length > 12) break;
      o.getWorldPosition(wp);
      if (Math.abs(wp.x - this.center.x) > 3 || Math.abs(wp.z - this.center.z) > 3) continue;   // 멀면 Box3 도 안 잰다
      box.setFromObject(o);
      if (box.isEmpty() || !box.intersectsBox(near)) continue;
      out.push({ min: box.min.clone(), max: box.max.clone() });
    }
    this.obstacles = out;
    this.obstacleAge = 0;
  }

  step(dt: number, t: number, viewer: THREE.Vector3 | null, pieces: THREE.Object3D[]) {
    if ((this.obstacleAge += dt) > 1.0) this.refreshObstacles(pieces);
    const s = this.spec, pos = this.pos, prev = this.prev;
    const h = dt / SUBSTEPS;
    const wind = 0.03 * Math.sin(t * 1.3 + this.phase) + 0.02 * Math.sin(t * 2.7 + this.phase * 0.7);   // 스치는 바람 — 「찰랑」
    const wx = s.inward.x * wind, wz = s.inward.z * wind;
    const damp = 0.985;
    for (let sub = 0; sub < SUBSTEPS; sub++) {
      // 적분 (윗단은 고정)
      for (let i = COLS; i < this.n; i++) {
        const k = i * 3;
        const vx = (pos[k] - prev[k]) * damp, vy = (pos[k + 1] - prev[k + 1]) * damp, vz = (pos[k + 2] - prev[k + 2]) * damp;
        prev[k] = pos[k]; prev[k + 1] = pos[k + 1]; prev[k + 2] = pos[k + 2];
        pos[k] += vx + wx * h * h * 40; pos[k + 1] += vy + GRAV * h * h; pos[k + 2] += vz + wz * h * h * 40;
      }
      // 제약
      for (let it = 0; it < ITERS; it++) {
        for (const c of this.cons) {
          const a = c.a * 3, b = c.b * 3;
          let dx = pos[b] - pos[a], dy = pos[b + 1] - pos[a + 1], dz = pos[b + 2] - pos[a + 2];
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1e-6;
          const diff = ((d - c.rest) / d) * c.k * 0.5;
          dx *= diff; dy *= diff; dz *= diff;
          const pa = c.a < COLS, pb = c.b < COLS;
          if (!pa && !pb) { pos[a] += dx; pos[a + 1] += dy; pos[a + 2] += dz; pos[b] -= dx; pos[b + 1] -= dy; pos[b + 2] -= dz; }
          else if (pa && !pb) { pos[b] -= dx * 2; pos[b + 1] -= dy * 2; pos[b + 2] -= dz * 2; }
          else if (!pa && pb) { pos[a] += dx * 2; pos[a + 1] += dy * 2; pos[a + 2] += dz * 2; }
        }
        // 쉼 자세로 되돌아오는 스프링 — 밀려도 주름이 무너지지 않는다
        const R = this.rest, kS = 0.06;
        for (let i = COLS * 3; i < this.n * 3; i++) pos[i] += (R[i] - pos[i]) * kS;
        this.collide(viewer);
      }
    }
    (this.geo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    this.geo.computeVertexNormals();
  }

  private collide(viewer: THREE.Vector3 | null) {
    const s = this.spec, pos = this.pos;
    const fy = s.floorY + 0.012;
    const wallPlane = s.top.dot(s.inward) - s.wallBack + 0.035;   // 이보다 안쪽(inward 성분이 큰 쪽)에 있어야 한다
    for (let i = COLS; i < this.n; i++) {
      const k = i * 3;
      // 바닥
      if (pos[k + 1] < fy) pos[k + 1] = fy;
      // 벽면
      const din = pos[k] * s.inward.x + pos[k + 2] * s.inward.z;
      if (din < wallPlane) { const push = wallPlane - din; pos[k] += s.inward.x * push; pos[k + 2] += s.inward.z * push; }
      // 가구 — **뒤로 보낸다** (Chris 2026-09-15: 「책상 딱 붙여도 실제처럼 뒤에 있는 것처럼」).
      // 가장 얕은 면으로 밀어내면 천이 책상 위로 올라앉는다. 실제 커튼은 책상과 벽 사이로 눌려 들어가
      // 책상 뒤 모서리에서 사라진다. 그래서 가구 안에 든 입자는 벽 쪽으로, 가구 뒷면과 벽 사이 틈으로
      // 보낸다. 틈이 없으면(가구가 벽에 딱 붙음) 벽면 바로 앞에 둔다 — 가구 속이라 안 보인다
      for (let oi = 0; oi < this.obstacles.length; oi++) {
        const o = this.obstacles[oi];
        const x = pos[k], y = pos[k + 1], z = pos[k + 2];
        if (x < o.min.x || x > o.max.x || y < o.min.y || y > o.max.y || z < o.min.z || z > o.max.z) continue;
        if (oi === 0) { pos[k + 1] = o.max.y + 0.01; continue; }   // 창턱은 위로 (닿을 일이 거의 없다)
        const back = Math.min(o.min.x * s.inward.x, o.max.x * s.inward.x) + Math.min(o.min.z * s.inward.z, o.max.z * s.inward.z);   // 가구 뒷면의 inward 거리
        const want = Math.max(wallPlane, back - 0.012);
        const cur = x * s.inward.x + z * s.inward.z;
        if (cur > want) { const push = cur - want; pos[k] -= s.inward.x * push; pos[k + 2] -= s.inward.z * push; }
      }
      // 사람 — 눈 위치 기준 캡슐(가슴·허리 두 구)
      if (viewer) {
        for (const [oy, r] of [[-0.45, 0.32], [-1.05, 0.3]] as const) {
          const cx = viewer.x, cy = viewer.y + oy, cz = viewer.z;
          let dx = pos[k] - cx, dy = pos[k + 1] - cy, dz = pos[k + 2] - cz;
          const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (d < r && d > 1e-5) { const p = (r - d) / d; pos[k] += dx * p; pos[k + 1] += dy * p * 0.3; pos[k + 2] += dz * p; }
        }
      }
    }
  }

  dispose() { this.geo.dispose(); }
}

export class CurtainSystem {
  panels: CurtainPanel[] = [];
  private t = 0;
  private pieces: THREE.Object3D[] = [];
  private pieceAge = 9;
  /** 조각 목록이 바뀌었을 때(에디터 소환·삭제) */
  markDirty() { this.pieceAge = 9; }
  clear(group?: THREE.Object3D) {
    for (const p of this.panels) { p.mesh.removeFromParent(); p.dispose(); }
    this.panels.length = 0;
    void group;
  }
  add(spec: CurtainSpec, parent: THREE.Object3D, slideGroup?: THREE.Object3D) {
    const p = new CurtainPanel(spec);
    parent.add(p.mesh);
    p.slideGroup = slideGroup ?? null;
    this.panels.push(p);
    return p;
  }
  /**
   * 매 프레임. viewer 는 눈 위치(게임은 카메라, 에디터도 카메라).
   * **가까운 폭 4개만** 계산한다. 쉼 자세가 곧 평형이라 나머지는 손대지 않아도 그 모양 그대로다.
   */
  update(dt: number, viewer: THREE.Vector3 | null, root: THREE.Object3D) {
    if (dt <= 0 || !viewer || !this.panels.length) return;
    const step = Math.min(dt, 1 / 30);
    this.t += step;
    /* 상호작용 슬라이드 — attachToPart 가 open:폭 그룹 위에 끼운 경첩(propId 가 있다)의
     * 로컬 x 가 밀린 거리다. 경첩이 없으면(attach 안 된 호스트·에디터) 부모가 호스트라 0. */
    for (const p of this.panels) {
      const g = p.slideGroup, par = g?.parent as THREE.Object3D | null | undefined;
      const s = par && par.userData.propId ? par.position.x : 0;
      const d = s - p.slideApplied;
      if (Math.abs(d) > 1e-6) { p.slideApplied = s; p.translate(d); }
    }
    if ((this.pieceAge += step) > 2) {   // 씬 훑기는 2초에 한 번, 시스템 전체에서 한 번
      this.pieces.length = 0;
      root.traverse((o) => { if (o.name.startsWith('piece:')) this.pieces.push(o); });
      this.pieceAge = 0;
    }
    const near: { p: CurtainPanel; d: number }[] = [];
    for (const p of this.panels) { const d = p.distanceTo(viewer); if (d < 9) near.push({ p, d }); }
    near.sort((a, b) => a.d - b.d);
    for (let i = 0; i < near.length && i < 4; i++) near[i].p.step(step, this.t, viewer, this.pieces);
  }
}
